/**
 * The menu a right-click or a ⋯ opens, at a point on the screen.
 *
 * Its own module because it is the app's menu, not the table's: the stat-card
 * strip opens it from a ⋯, a saved-view tab opens it from a right-click, and
 * `menu-select.tsx` is built to the same card so a dropdown and a context menu
 * are recognisably one thing.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { Keys } from './keycap.js'

export interface MenuItem {
  key: string
  label: string
  /** The shortcut, drawn right-aligned and muted. */
  hint?: string
  /** `primary` is the record's next action; `danger` is the destructive one. */
  tone?: 'plain' | 'primary' | 'danger'
  separatorBefore?: boolean
  /**
   * A toggle: ✓ in the gutter when on. Any item in a menu carrying it gives
   * the whole menu a check gutter, so the labels stay in one column (Board 7's
   * visit menu, where **Optional** is one item that is ticked or not).
   */
  checked?: boolean
  /** A second line under the label, 11.5 and muted. */
  sub?: string
  /**
   * A glyph before the label.
   *
   * **With `keys`** it is the menu's featured row — tinted, the label in the
   * accent, its keys as keycaps on the right — *New request for Dana*
   * (`new-request.md` §13.3, Board 1 H).
   *
   * **Without `keys`** it is folder 52's ⋯ row (`job-stages.md` §1, Boards 1
   * and 2): the glyph muted, the label 13 semibold, `sub` one muted line
   * under it, untinted — and a menu holding one is 330 wide with `line`
   * dividers. Items without a glyph render exactly as they always have.
   */
  icon?: ReactNode
  /** Keys, drawn as keycaps on a featured row: `['N', 'R']`. */
  keys?: string[]
  /**
   * A colour dot before the label — an Inbox category (`docs/activity.md`
   * §7.10). A CSS colour, never a hex typed at the call site: the marks
   * palette's `markInk()`.
   */
  swatch?: string
  onSelect: () => void
}

/**
 * The right-click menu, on the header and on a row.
 *
 * Positioned where the pointer was and nudged back inside the viewport, which
 * is the whole of what a menu at the edge of the screen needs.
 */
/**
 * The menu a right-click or a ⋯ opens, at a point on the screen.
 *
 * Exported because the stat-card strip opens the same menu from its own ⋯,
 * and a second menu component would drift from this one on padding, dismiss
 * behaviour and how a destructive item is coloured.
 */
export function ContextMenu({
  items, x, y, onClose, focusFirst = false, label,
}: {
  items: MenuItem[]; x: number; y: number; onClose: () => void
  /** Opened from the keyboard (the context-menu key, Shift+F10): the first item takes the focus. */
  focusFirst?: boolean
  label?: string
}) {
  const box = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState({ x, y })

  useLayoutEffect(() => {
    const rect = box.current?.getBoundingClientRect()
    if (!rect) return
    setAt({
      x: Math.min(x, window.innerWidth - rect.width - 8),
      y: Math.min(y, window.innerHeight - rect.height - 8),
    })
  }, [x, y])

  useEffect(() => {
    const dismiss = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) onClose()
    }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    // A frame late, or the click that opened it closes it again.
    const timer = window.setTimeout(() => window.addEventListener('mousedown', dismiss), 0)
    window.addEventListener('keydown', escape)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('mousedown', dismiss)
      window.removeEventListener('keydown', escape)
    }
  }, [onClose])

  useEffect(() => {
    if (focusFirst) box.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus()
  }, [focusFirst])

  /* Up and down walk the items, Home and End jump, Tab leaves — a menu's keys. */
  const walk = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const all = [...(box.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])]
    if (event.key === 'Tab') { onClose(); return }
    const index = all.indexOf(document.activeElement as HTMLElement)
    const next = event.key === 'ArrowDown' ? (index + 1) % all.length
      : event.key === 'ArrowUp' ? (index <= 0 ? all.length - 1 : index - 1)
        : event.key === 'Home' ? 0 : event.key === 'End' ? all.length - 1 : null
    if (next === null || all.length === 0) return
    event.preventDefault()
    all[next]!.focus()
  }

  const gutter = items.some((item) => item.checked !== undefined)
  const isFeatured = (item: MenuItem) => item.icon !== undefined && item.keys !== undefined
  /* Folder 52's ⋯ row: a glyph and no keys (Boards 1 and 2). */
  const isStaged = (item: MenuItem) => item.icon !== undefined && item.keys === undefined
  const featured = items.some(isFeatured)
  const staged = items.some(isStaged)
  return (
    <div
      ref={box}
      role="menu"
      aria-label={label}
      onKeyDown={walk}
      className={cn('fixed z-50 border border-line bg-surface py-1 shadow-popover',
        staged ? 'w-[330px] rounded-touch leading-[normal]'
          : featured ? 'w-[320px] rounded-card' : 'min-w-[208px] rounded-card')}
      style={{ left: at.x, top: at.y }}
    >
      {items.map((item) => (
        <Fragment key={item.key}>
          {item.separatorBefore && (
            <div className={cn('my-1 border-t', staged ? 'border-line' : 'border-line-soft')} />
          )}
          {isStaged(item) ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => { item.onSelect(); onClose() }}
              className="tnum mx-1 flex w-[calc(100%-8px)] items-center gap-[11px] rounded-[9px] px-3 py-[9px] text-left hover:bg-line-soft"
            >
              <span className="inline-flex shrink-0 text-text-muted">{item.icon}</span>
              <span className="flex min-w-0 flex-1 flex-col">
                <b className="text-[13px] font-semibold text-text">{item.label}</b>
                {item.sub && <span className="text-[11.5px] text-text-muted">{item.sub}</span>}
              </span>
            </button>
          ) : item.icon !== undefined ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => { item.onSelect(); onClose() }}
              className="mx-1 flex w-[calc(100%-8px)] items-center gap-[11px] rounded-[9px] bg-green-band-bg px-3 py-[9px] text-left hover:bg-accent-soft"
            >
              <span className="inline-flex shrink-0 text-accent">{item.icon}</span>
              <span className="flex min-w-0 flex-1 flex-col">
                <b className="truncate text-[13px] font-semibold text-accent-dark">{item.label}</b>
                {item.sub && <span className="text-[11.5px] leading-[1.35] text-text-muted">{item.sub}</span>}
              </span>
              {item.keys && <Keys keys={item.keys} />}
            </button>
          ) : (
          <button
            type="button"
            role={item.checked !== undefined ? 'menuitemcheckbox' : 'menuitem'}
            aria-checked={item.checked}
            onClick={() => { item.onSelect(); onClose() }}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-1.5 text-left text-cell',
              item.tone === 'danger' ? 'text-danger hover:bg-danger-bg'
                : item.tone === 'primary' ? 'bg-accent-soft font-semibold text-accent-dark hover:bg-accent-soft'
                  : 'text-text hover:bg-line-soft',
            )}
          >
            {gutter && (
              <span aria-hidden className="w-3.5 shrink-0 self-start text-accent">
                {item.checked ? '✓' : ''}
              </span>
            )}
            {item.swatch && (
              <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: item.swatch }} />
            )}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{item.label}</span>
              {item.sub && (
                <span className="truncate text-[11.5px] text-text-muted">{item.sub}</span>
              )}
            </span>
            {item.hint && <span className="shrink-0 text-[10px] text-text-faint">{item.hint}</span>}
          </button>
          )}
        </Fragment>
      ))}
    </div>
  )
}
